// Client-side (renderer) generate/parse pair for a local-only production
// sheet's real .xlsx file -- openspec/changes/local-production-sheets-and-
// path-mentions, task 2.x.
//
// Deliberately NOT the server's own generateSheetXlsx/parseSheetXlsx
// (server/utils/productionExport.js, which uses `exceljs`): that module is
// CommonJS meant for the Node server process, and `exceljs` has no
// out-of-the-box browser build -- it needs `stream`/`fs`-shim webpack
// polyfills this app's craco.config.js doesn't configure (only `buffer` is
// polyfilled today, added for isomorphic-git). Rather than expanding that
// polyfill surface for one feature, this uses `xlsx` (SheetJS) instead --
// the standard zero-config browser spreadsheet library, already how most
// web apps read/write real .xlsx client-side. Both libraries write
// spec-compliant OOXML, so a file this writes opens identically in Excel/
// Google Sheets/LibreOffice, and (for the plain single-sheet, no-merge-only
// case both sides actually exercise) parses back the same way the server's
// own `parseSheetXlsx` would if the file ever round-trips through a cloud
// re-sync later.
//
// Security note: `xlsx@0.18.5` (the latest release published to the npm
// registry -- SheetJS moved newer patched releases to their own CDN outside
// npm) has known prototype-pollution/ReDoS advisories in its parser. Usage
// here is scoped to files this app itself already wrote to the user's own
// local disk (never a third-party upload), which bounds but does not
// eliminate the exposure -- worth revisiting if this ever needs to parse a
// sheet from an untrusted source.
//
// Mirrors productionExport.js's "current format" convention exactly: the
// SpreadsheetEditor {headers, rows, merges} JSON blob a production sheet's
// `content` field already holds -- header row = columns, each `rows` entry
// a plain array of cell values in column order. The legacy relational
// {columns, rows, cells} shape is NOT handled here (that's a pre-blob-format
// migration concern the local-only path never needs to deal with, since a
// sheet must already be loaded as a live {headers, rows, merges} blob to be
// open in the editor at all).

import * as XLSX from 'xlsx';

const SHEET_NAME_MAX = 31; // Excel worksheet name hard limit
const FORBIDDEN_SHEET_CHARS = /[:\\/?*[\]]/g;

function sanitizeSheetName(name) {
  const cleaned = (name || 'Sheet').replace(FORBIDDEN_SHEET_CHARS, ' ').trim();
  return (cleaned || 'Sheet').slice(0, SHEET_NAME_MAX);
}

/**
 * {name, content: JSON string of {headers, rows, merges}} -> real .xlsx
 * bytes (Uint8Array), ready to hand to localFileService's base64+write path.
 * Never throws on malformed/missing content -- falls back to an
 * empty-but-valid workbook, matching the tolerant spirit of the server's
 * own parseSheetXlsx.
 */
export function generateLocalSheetXlsx({ name, content }) {
  let headers = [];
  let rows = [];
  try {
    const parsed = content ? JSON.parse(content) : null;
    if (Array.isArray(parsed?.headers)) {
      headers = parsed.headers;
      rows = Array.isArray(parsed.rows) ? parsed.rows : [];
    }
  } catch { /* not the expected JSON shape -- fall through to an empty sheet */ }

  const aoa = [headers, ...rows];
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  worksheet['!cols'] = headers.map((h) => ({ wch: Math.max(10, String(h || '').length + 4) }));

  const workbook = XLSX.utils.book_new();
  workbook.Props = { Author: 'Tarr' };
  XLSX.utils.book_append_sheet(workbook, worksheet, sanitizeSheetName(name));

  // SheetJS's own `type: 'array'` returns a plain ArrayBuffer in some
  // environments (confirmed live: Node/jsdom test runs) and a Uint8Array in
  // others -- wrap explicitly so every caller (localFileService.js's
  // Buffer.from(...), this module's own tests) gets one guaranteed type
  // rather than branching on which environment produced it.
  const written = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
  return written instanceof Uint8Array ? written : new Uint8Array(written);
}

// Coerce one SheetJS cell value into a plain string -- same defensive
// posture as the server's cellValueToString, though SheetJS's own
// sheet_to_json already normalizes most of this for us.
function cellToString(raw) {
  if (raw === undefined || raw === null) return '';
  if (raw instanceof Date) return raw.toISOString();
  return String(raw);
}

/**
 * Inverse of generateLocalSheetXlsx: a real .xlsx buffer (ArrayBuffer /
 * Uint8Array) -> {headers, rows} in the exact shape SpreadsheetEditor's
 * content blob already uses (no `merges` -- a sheet hand-edited outside the
 * app has no concept of Tarr's own merge metadata to recover; a real Excel
 * merged-cell region is simply read as its top-left value with the other
 * cells blank, same as opening any foreign .xlsx). Defensive against a
 * missing/corrupt file, no worksheets, no header row, and ragged rows --
 * skip/tolerate rather than throw, matching localFileService.js's existing
 * tolerance for a corrupt local file and the server's own parseSheetXlsx.
 */
export function parseLocalSheetXlsx(buffer) {
  let workbook;
  try {
    workbook = XLSX.read(buffer, { type: 'array' });
  } catch {
    return { headers: [], rows: [] };
  }

  const sheetName = workbook.SheetNames?.[0];
  const worksheet = sheetName ? workbook.Sheets[sheetName] : null;
  if (!worksheet) return { headers: [], rows: [] };

  let aoa;
  try {
    aoa = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', blankrows: false });
  } catch {
    return { headers: [], rows: [] };
  }
  if (!Array.isArray(aoa) || aoa.length === 0) return { headers: [], rows: [] };

  const headers = (aoa[0] || []).map((h) => cellToString(h));
  // A genuinely corrupt/non-xlsx buffer can still "succeed" through
  // XLSX.read/sheet_to_json with a degenerate result (confirmed live: 5
  // raw garbage bytes parsed into one "header" cell whose string value
  // LOOKS blank when printed but is actually a non-printable control
  // character, not a real empty string -- a plain `=== ''` check missed
  // it). Strip control characters and whitespace before judging a header
  // blank, so real garbage is rejected the same way a truly empty header
  // row already is.
  const isBlankHeader = (h) => h.replace(/[\x00-\x1F\x7F]/g, '').trim() === '';
  if (headers.length === 0 || headers.every(isBlankHeader)) return { headers: [], rows: [] };

  const rows = aoa.slice(1)
    .map((row) => headers.map((_h, idx) => cellToString(row[idx])))
    .filter((row) => row.some((v) => v !== ''));

  return { headers, rows };
}
